import { useElements, useStripe } from "@stripe/react-stripe-js";
import { useFormik, FormikHelpers, yupToFormErrors } from "formik";
import {
  ShippingOption,
  StripePaymentMethod,
  UpdateOrderInput,
} from "~graphql/sdk";
import { useContext, useEffect, useState } from "react";
import { useSDK } from "~hooks";
import { getError, handlePromise, showToast } from "~lib";
import * as Yup from "yup";
import { checkoutDebug } from "~lib/debug";
import { StripeExpressCheckoutElementConfirmEvent } from "@stripe/stripe-js";
import { useStripeConfigContext } from "~lib/stripeConfigContext";
import { extractCardholderName } from "~lib/helpers/extractCardHolderName";
import camelCase from "lodash/camelCase";
import { useSinglePageCheckoutContext } from "~features/checkout/common/hooks/singlePageCheckoutContext";
import upperFirst from "lodash/upperFirst";
import { handleSentryError } from "../errors.utils";
import * as PrePurchaseQuestions from "~features/checkout/common/components/PrePurchaseQuestionsFormPartial";
import * as OptInToMarketing from "~features/checkout/common/components/OptInToMarketingFormPartial";
import * as TicketCover from "~features/checkout/common/components/TicketCoverWithQueue";
import { BaseContext } from "~context";
export interface ExpressPageCheckoutFormValues
  extends PrePurchaseQuestions.FormValues,
    OptInToMarketing.FormValues,
    TicketCover.FormValues {}

export const useExpressCheckoutForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const sdk = useSDK();
  const { order } = useSinglePageCheckoutContext();
  const { organization } = useContext(BaseContext);
  const config = useStripeConfigContext();
  const [paymentInProgress, setPaymentInProgress] = useState(false);

  const handleSubmit = (
    values: ExpressPageCheckoutFormValues,
    actions: FormikHelpers<ExpressPageCheckoutFormValues>
  ) => {
    // No-op because we handle submission via the Stripe Express Checkout confirm event
  };

  const optInToMarketingInitialValues = OptInToMarketing.useInitialValues();

  const initialValues = (): ExpressPageCheckoutFormValues => ({
    ...PrePurchaseQuestions.initialValues(),
    ...TicketCover.initialValues(),
    ...optInToMarketingInitialValues,
  });

  const formik = useFormik<ExpressPageCheckoutFormValues>({
    initialValues: initialValues(),
    onSubmit: handleSubmit,
    validate: async (values) => {
      const requireTicketCoverSelection = TicketCover.getRequireTicketCoverSelection(
        order?.event?.ticketCover
      );

      const { askHowdidYouhearAboutUs } = PrePurchaseQuestions.getActiveFields(
        order,
        organization.id
      );

      const validationContext = {
        askHowdidYouhearAboutUs,
        requireTicketCoverSelection,
      };

      const validationSchema: Yup.ObjectSchema<ExpressPageCheckoutFormValues> = PrePurchaseQuestions.schema
        .concat(OptInToMarketing.schema)
        .concat(TicketCover.schema);

      try {
        await validationSchema.validate(values, {
          abortEarly: false,
          context: validationContext,
        });

        return undefined; // No errors
      } catch (error) {
        checkoutDebug("Validation failed", {
          error,
        });
        return yupToFormErrors(error);
      }
    },
    validateOnChange: false,
  });

  // Scroll to first error when submitting the form
  useEffect(() => {
    if (formik.isSubmitting && Object.keys(formik.errors).length > 0) {
      const firstErrorField = Object.keys(formik.errors)[0];

      const errorElement = document.getElementById(firstErrorField);

      if (errorElement) {
        // Focus the element (for inputs)
        errorElement.focus?.();

        errorElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [formik.isSubmitting, formik.errors]);

  async function handleConfirm(e: StripeExpressCheckoutElementConfirmEvent) {
    checkoutDebug("confirmed", e);

    const { error: submitError } = await elements.submit();

    if (submitError) {
      console.error(submitError);
      handleSentryError(
        "Error submitting Stripe Express Checkout Element",
        submitError
      );
      return;
    }

    const { firstName, lastName } = extractCardholderName(
      e.billingDetails?.name
    );

    const prePurchaseQuestionValues: Partial<UpdateOrderInput> = {
      dietaryRequirements: formik.values.dietaryRequirements,
      heardAboutUs: formik.values.heardAboutUs,
      heardAboutUsDetails: formik.values.heardAboutUsDetails,
    };

    const updateOrderInput: UpdateOrderInput = {
      step: 3, // Skips buyer information validation, which is step 2
      deliveryMethod: ShippingOption.Eticket,
      buyerInformation: {
        // This is the name on the card
        firstName,
        lastName,
        email: e.billingDetails?.email,
        phoneNumber: e.billingDetails?.phone ?? "0000",

        state: emptyStringToUndefined(e.billingDetails?.address?.state),
        postalCode: emptyStringToUndefined(
          e.billingDetails?.address?.postal_code
        ),
        country: emptyStringToUndefined(e.billingDetails?.address?.country),
        city: emptyStringToUndefined(e.billingDetails?.address?.city),
      },
      billingAddress: {
        line1: emptyStringToUndefined(e.billingDetails?.address?.line1),
        line2: emptyStringToUndefined(e.billingDetails?.address?.line2),
        suburb: undefined,
        postalCode: emptyStringToUndefined(
          e.billingDetails?.address?.postal_code
        ),
        city: emptyStringToUndefined(e.billingDetails?.address?.city),
        state: emptyStringToUndefined(e.billingDetails?.address?.state),
        country: emptyStringToUndefined(e.billingDetails?.address?.country),
      },
      ...prePurchaseQuestionValues,
    };

    const { error: updateOrderError } = await handlePromise(async () =>
      sdk.updateOrder({
        id: order.id,
        input: updateOrderInput,
      })
    );

    if (updateOrderError) {
      handleSentryError(
        "Unable to update order with buyer information (full details)",
        updateOrderError
      );

      // Capturing the full details has failed (presumably due to validation), retry with minimal required details.
      const { error } = await handlePromise(async () =>
        sdk.updateOrder({
          id: order.id,
          input: {
            step: 3,
            deliveryMethod: ShippingOption.Eticket,
            buyerInformation: {
              firstName,
              lastName,
              email: e.billingDetails?.email,
              phoneNumber: e.billingDetails?.phone ?? "0000",
            },
          },
        })
      );

      if (error) {
        handleSentryError(
          "Unable to update order with buyer information (email)",
          error
        );
      }
    }

    const { error: createPaymentError, data } = await handlePromise(async () =>
      sdk.createPayment({
        id: order.id,
        input: {
          gateway: config.integrationId,
          usableForMarketing: formik.values.usableForMarketing,
          gatewayOptions: {
            expressCheckoutPaymentMethod: upperFirst(
              camelCase(e.expressPaymentType)
            ) as StripePaymentMethod,
          },
        },
      })
    );

    if (createPaymentError) {
      handleSentryError("Unable to create payment", createPaymentError);
      return showToast(getError(createPaymentError, "graphQL"), "error");
    }

    if (data.createPayment.result?.__typename === "PaymentError") {
      handleSentryError(
        "Payment error returned from createPayment",
        data.createPayment.result.message
      );
      return showToast(data.createPayment.result.message, "error");
    }

    const redirectTo = process.env.STRIPE_EXPRESS_CHECKOUT_TEST_DOMAIN
      ? `${process.env.STRIPE_EXPRESS_CHECKOUT_TEST_DOMAIN}/checkout/${order.id}`
      : data.createPayment?.result?.redirectTo;

    setPaymentInProgress(true);

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        clientSecret: data.createPayment?.result?.externalId,
        confirmParams: {
          return_url: redirectTo + "?expressCheckout=true",
        },
      });

      if (error) {
        // This point is only reached if there's an immediate error when
        // confirming the payment. Show the error to your customer (for example, payment details incomplete)
        const msg =
          error.message || "Could not confirm payment. Please try again";
        showToast(msg, "error");

        handleSentryError("Error confirming payment with Stripe", error, {
          code: error.code,
        });
        setPaymentInProgress(false);

        console.error(error);
      } else {
        // The payment UI automatically closes and the customer is redirected.
      }
    } catch (err) {
      console.error(err);
      showToast("Could not confirm payment. Please try again", "error");
      handleSentryError("Exception confirming payment with Stripe", err);
      setPaymentInProgress(false);
    }
  }

  return {
    formik,
    handleConfirm,
    paymentInProgress,
  };
};

function emptyStringToUndefined(
  str: string | undefined | null
): string | undefined {
  if (typeof str === "string" && str.trim() === "") {
    return undefined;
  }
  return str ?? undefined;
}
